import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Filters,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  useGetList,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  formatDate,
  SchemaFilterType,
  usePrevious,
  IconRight,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/list';
import { useLazyGetAnonymisationLogsQuery } from './store/service';
import { AnonymisationLog, AnonymisationLogEntityType } from './types';

import cn from './styles.module.css';

const formatAnonymisationLogsList = (
  list: AnonymisationLog[],
): AnonymisationLog[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};

    const link =
      result.entityType === AnonymisationLogEntityType.CASH_COLLECTION
        ? `/transactions/cash-collection/list/${result?.entityId}`
        : `/transactions/cash-collection/orders/beneficiary/${result?.entityId}`;

    render.anonymizedAt = (
      <div>{result.anonymizedAt ? formatDate(result.anonymizedAt) : '-'}</div>
    );
    render.entityType = (
      <div>
        {result.entityType === AnonymisationLogEntityType.CASH_COLLECTION
          ? 'Cash Collection'
          : 'Orders'}
      </div>
    );

    render.entityId = (
      <div>
        <NavLink to={link} target="_blank" className={cn.entityLink}>
          {result.entityId}
          <IconRight width={12} height={12} />
        </NavLink>
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const AnonymisationLogs = () => {
  const [takeCount, setTakeCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    anonymisationLogs: { anonymisationLogs, pagination, errorsList },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getList, { isFetching, isLoading }] =
    useLazyGetAnonymisationLogsQuery();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const formattedData = useMemo(() => {
    const data = formatAnonymisationLogsList(
      anonymisationLogs as AnonymisationLog[],
    );

    return data;
  }, [anonymisationLogs]);

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<AnonymisationLog>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  useEffect(() => {
    if (
      takeCount !== 0 &&
      filters &&
      !anonymisationLogs &&
      prevFilters &&
      !Object.keys(prevFilters).length
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: { value: [takeCount?.toString()], type: SchemaFilterType.STRING },
      };

      if (!nextFilters.entityType) {
        nextFilters.entityType = {
          value: [
            AnonymisationLogEntityType.CASH_COLLECTION,
            AnonymisationLogEntityType.CASH_COLLECTION_BENEFICIARY,
          ],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({ filters: nextFilters, sort });

      onFilter(nextFilters);
    }
  }, [filters, anonymisationLogs, takeCount, prevFilters]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        setTakeCount(Math.ceil(take + 3));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    let title = 'Anonymisation';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (activePermissions?.includes(PermissionEnum.ANONYMIZE_LOGS_LIST)) {
    if (isLoading || takeCount === 0) {
      content = (
        <div className={cn.tabLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={filters}
          sort={sort}
          columnsLabels={columnsLabels}
          onFilterChange={(filterValues) => {
            const nextFilters = { ...filterValues };

            if (!nextFilters.entityType?.value?.length) {
              nextFilters.entityType = {
                value: [
                  AnonymisationLogEntityType.CASH_COLLECTION,
                  AnonymisationLogEntityType.CASH_COLLECTION_BENEFICIARY,
                ],
                type: SchemaFilterType.STRING,
              };
            }

            onFilter({
              ...nextFilters,
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            });
          }}
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isFetching}
          take={takeCount.toString()}
          error={errorsList || errorList}
          hiddenFiltersTags={
            filters?.entityType?.value.length === 2 ? ['entityType'] : []
          }
          setPage={(data) => {
            const nextFilters = { ...filters };

            if (!nextFilters.entityType?.value?.length) {
              nextFilters.entityType = {
                value: [
                  AnonymisationLogEntityType.CASH_COLLECTION,
                  AnonymisationLogEntityType.CASH_COLLECTION_BENEFICIARY,
                ],
                type: SchemaFilterType.STRING,
              };
            }

            setPage({
              ...data,
              filters: {
                ...nextFilters,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
              },
            });
          }}
          pagination={pagination}
          withScroll
        />
      );
    }
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.anonymisationWrapper}>
      <div className={cn.anonymisationTitle}>Anonymisation</div>
      <div className={cn.anonymisationContentWrapper} ref={scrollRef}>
        {content}
      </div>
    </div>
  );
};

export default AnonymisationLogs;
